import { Suspense, useEffect } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { addErrorInterceptor } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { router } from '@/routes';
import { useTheme } from '@/hooks/useTheme';

let isHandlingUnauthorized = false;

/**
 * Loading fallback component for code-split routes
 * Displays centered spinner during lazy component loading
 */
function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"
          role="status"
          aria-label="Loading page"
        />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  // P1-UI-05: Initialize theme hook for system preference sync
  useTheme();

  useEffect(() => {
    const removeInterceptor = addErrorInterceptor(async (error) => {
      if (error.status !== 401 || isHandlingUnauthorized) {
        return error;
      }

      if (window.location.pathname.startsWith('/login')) {
        return error;
      }

      // If not authenticated, 401 is expected (e.g. LP visitor calling /auth/me)
      // Only redirect when a previously authenticated session expires
      if (!useAuthStore.getState().isAuthenticated) {
        return error;
      }

      isHandlingUnauthorized = true;
      useAuthStore.getState().logout();

      const redirectTarget = `${window.location.pathname}${window.location.search}`;
      void router.navigate({
        to: '/login',
        search: {
          redirect: redirectTarget,
        },
      } as never);

      return error;
    });

    return () => {
      removeInterceptor();
      isHandlingUnauthorized = false;
    };
  }, []);

  return (
    <>
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
      <Toaster position="top-right" richColors closeButton />
    </>
  );
}

export default App;
